import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { NGXLogger as LoggerService } from 'ngx-logger';
import { AngularFireDatabase, AngularFireObject } from '@angular/fire/compat/database';
import { HeaderData, PollKeysClass } from 'whocan-lib';
import { paths } from 'functions-lib';
import { PollService } from '../../core/poll.service';
import { NavigationService } from '../../core/navigation.service';
import { GoogleAnalyticsService } from '../../../shared/services/google-analytics.service';
import { EmbedModeService } from '../../../shared/services/embed-mode.service';

/**
 * Kopiert einen Sample-Poll fuer einen User (typischerweise via 'Kopiere und bearbeite'-CTA).
 *
 * Ablauf:
 * 1. PollService.copyPoll() erzeugt Header- und Options-Kopie unter dem aktuellen User.
 *    Falls noch nicht eingeloggt (Listing /poll-templates ohne Session), legt copyPoll
 *    selbst zuerst einen anonymen Account an — gleiche Mechanik wie persistNewPoll.
 *    Auf der Detailseite besteht die Session schon (initPollAndSubscribeData).
 * 2. Sample-spezifische Flags werden im neuen Header geloescht, damit die Kopie ein
 *    normaler User-Poll wird (nicht versehentlich noch als Sample erscheinen).
 * 3. Navigation zur Vote-Preview-Page mit `?edit=true` (identischer Flow wie
 *    "Bearbeiten" in my-polls — Sidenav aufgeklappt, kein voller Stepper-Restart).
 *
 * Votes werden vom existierenden copyPoll bewusst NICHT mit-kopiert.
 *
 * Wird sowohl von der Sample-Detail-Page (Vote-Result-View) als auch von der
 * Sample-Polls-Liste (Quick-Action pro Card) aufgerufen.
 */
@Injectable({
    providedIn: 'root'
})
export class SamplePollCopyService {
    /**
     * Solange true: der Kopier-Vorgang laeuft (anonymer Signup falls noetig,
     * RTDB-Writes, Navigation). Komponenten zeigen darueber den Lade-Spinner
     * an, damit der User Feedback erhaelt, dass etwas passiert.
     */
    public isLoading = false;

    constructor(
        @Inject(LOCALE_ID) private locale: string,
        private logger: LoggerService,
        private pollService: PollService,
        private navigationService: NavigationService,
        private afd: AngularFireDatabase,
        private googleAnalytics: GoogleAnalyticsService,
        private embedMode: EmbedModeService,
    ) { }

    /**
     * Kopiert den Sample-Poll, raeumt die Sample-Flags in der Kopie auf,
     * und navigiert (oder oeffnet einen neuen Tab) in die Vote-Preview-Page
     * mit ?edit=true.
     *
     * Embed-Mode (iframe in einer Astro-Landing-Page):
     * - Wir oeffnen einen neuen Top-Level-Tab statt im iframe zu navigieren
     * - `window.open('about:blank', '_blank')` wird FRUEH aufgerufen (vor dem
     *   await), sonst killt der Popup-Blocker das Fenster, weil der
     *   User-Gesture-Context durch das Async-Warten verloren geht
     * - Nach dem Copy + Cleanup setzen wir `newTab.location.href` auf die
     *   finale URL
     *
     * Normaler Modus (volle App):
     * - Angular-Router-Navigation wie gewohnt
     */
    public async copyAndEdit(ownerId: string, pollId: string): Promise<void> {
        this.logger.log('SamplePollCopyService.copyAndEdit', ownerId, pollId, 'embed=', this.embedMode.isEmbedded);
        this.isLoading = true;

        // Im Embed-Mode: neuen Tab FRUEH oeffnen (mit about:blank), damit der
        // Browser ihn als User-Gesture-getriggerten Popup einstuft. Wenn wir
        // erst nach den awaits öffnen, blocken Chrome/Safari den Popup.
        const newTab: Window | null = this.embedMode.isEmbedded
            ? window.open('about:blank', '_blank')
            : null;

        try {
            const newKeys: PollKeysClass = await this.pollService.copyPoll(ownerId, pollId);
            if (!newKeys?.ownerId || !newKeys?.pollId) {
                this.logger.error('SamplePollCopyService: copyPoll returned invalid keys', newKeys);
                if (newTab) newTab.close();
                return;
            }
            await this.clearSampleFlags(newKeys.ownerId, newKeys.pollId);
            // GA-Event: Sample-Poll wurde als Vorlage kopiert. Im Embed-Mode
            // wird das ueber den isDisabled-Guard in GoogleAnalyticsService
            // automatisch unterdrueckt (kein Consent im iframe).
            this.googleAnalytics.emitSimpleEvent('sample_poll_copy');
            // copyPoll setzt newKeys.ownerId === authService.uid, also auch der voteUserId
            // der Bearbeiter-Variante.
            if (newTab) {
                newTab.location.href = this.buildVotePreviewUrl(newKeys.ownerId, newKeys.pollId);
            } else {
                this.navigationService.navigateVotePreview(
                    newKeys.ownerId,
                    newKeys.pollId,
                    newKeys.ownerId,
                    true,
                );
            }
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Absolute URL zur Vote-Preview-Page mit ?edit=true, inklusive Locale-Prefix
     * (z.B. https://www.whocan.org/de/vote-preview/owner/poll/voter?edit=true).
     * Wird im Embed-Mode an `window.open` uebergeben.
     */
    private buildVotePreviewUrl(newOwnerId: string, newPollId: string): string {
        const path = this.navigationService.getPathVotePreview(newOwnerId, newPollId, newOwnerId);
        const lang = (this.locale || 'en').substring(0, 2).toLowerCase();
        return window.location.origin + '/' + lang + path + '?edit=true';
    }

    /**
     * Loescht isSamplePoll, slug und pollLanguage aus dem kopierten Header.
     * Direkter RTDB-Schreibvorgang, damit der Headerstate des aktuellen Polls
     * im PollService nicht angefasst werden muss.
     */
    private async clearSampleFlags(newOwnerId: string, newPollId: string): Promise<void> {
        const path = paths.header + '/' + newOwnerId + '/' + newPollId;
        const ref: AngularFireObject<Partial<HeaderData>> = this.afd.object(path);
        await ref.update({
            isSamplePoll: false,
            slug: null as any,
            pollLanguage: null as any,
        });
        this.logger.log('SamplePollCopyService.clearSampleFlags done', path);
    }
}
